import { type CSSProperties, memo, type RefCallback } from "react";
import BookingListItem from "@/components/lists/listItems/BookingListItem/BookingListItem";
import type { Booking } from "@/logic/api";
import type { BookingKey, LiveBookingChanges } from "./getLiveBookingChanges";
import {
  type InitialBookingEntry,
  type LiveBookingMotion,
  useBookingViewMotion,
} from "./useBookingViewMotion";
import classes from "./styles.module.css";

const MemoBookingListItem = memo(BookingListItem);

type BookingEntryStyle = CSSProperties & {
  "--booking-enter-delay"?: string;
  "--booking-enter-distance"?: string;
};

interface BookingViewRowProps {
  booking: Booking;
  initialEntry?: InitialBookingEntry;
  isMeasuring: boolean;
  liveMotion?: LiveBookingMotion;
  shouldTrackBookingElements: boolean;
  getBookingRef(id: BookingKey): RefCallback<HTMLDivElement>;
  meetGreetCollectionDeliveryEnabled: boolean;
  finishLiveEntry(id: BookingKey, revision: number): void;
  finishLiveUpdatePhase(
    id: BookingKey,
    revision: number,
    phase: "rise" | "rearm" | "fade",
  ): void;
}

const BookingViewRow = ({
  booking,
  initialEntry,
  isMeasuring,
  liveMotion,
  shouldTrackBookingElements,
  getBookingRef,
  meetGreetCollectionDeliveryEnabled,
  finishLiveEntry,
  finishLiveUpdatePhase,
}: BookingViewRowProps) => {
  const shouldAnimateInitialEntry = initialEntry != null;
  const entryStyle: BookingEntryStyle | undefined = initialEntry
    ? {
      "--booking-enter-delay": `${initialEntry.delay}ms`,
      "--booking-enter-distance": initialEntry.distance,
    }
    : undefined;
  const className = [
    classes.bookingViewItemContent,
    isMeasuring ? classes.cardMeasuring : undefined,
    shouldAnimateInitialEntry ? classes.cardEntering : undefined,
    liveMotion?.kind === "enter" ? classes.cardLiveEntering : undefined,
  ]
    .filter(Boolean)
    .join(" ");
  const updateHighlightClass =
    liveMotion?.kind === "update"
      ? liveMotion.phase === "rise"
        ? classes.cardLiveUpdateHighlightActive
        : liveMotion.phase === "rearm"
          ? classes.cardLiveUpdateHighlightRearming
          : undefined
      : undefined;

  return (
    <div
      ref={shouldTrackBookingElements ? getBookingRef(booking.uuid) : undefined}
      className={classes.bookingViewItem}
      style={entryStyle}
      data-testid="booking-view-item"
      data-booking-uuid={booking.uuid}
      data-entry-motion={shouldAnimateInitialEntry ? "enter" : "none"}
      data-live-motion={liveMotion?.kind ?? "none"}
    >
      <div
        className={className}
        onAnimationEnd={
          liveMotion?.kind === "enter"
            ? (event) => {
              if (event.target === event.currentTarget) {
                finishLiveEntry(booking.uuid, liveMotion.revision);
              }
            }
            : undefined
        }
      >
        <MemoBookingListItem
          booking={booking}
          meetGreetCollectionDeliveryEnabled={
            meetGreetCollectionDeliveryEnabled
          }
        />
        <span
          className={[classes.cardLiveUpdateHighlight, updateHighlightClass]
            .filter(Boolean)
            .join(" ")}
          data-testid="booking-live-update-highlight"
          data-active={liveMotion?.kind === "update" ? "" : undefined}
          aria-hidden="true"
          onTransitionEnd={
            liveMotion?.kind === "update"
              ? (event) => {
                if (
                  event.target === event.currentTarget &&
                  event.propertyName === "opacity"
                ) {
                  finishLiveUpdatePhase(
                    booking.uuid,
                    liveMotion.revision,
                    liveMotion.phase,
                  );
                }
              }
              : undefined
          }
        />
      </div>
    </div>
  );
};

const MemoBookingViewRow = memo(BookingViewRow);

interface BookingViewListProps {
  bookings: ReadonlyArray<Booking>;
  animationsEnabled: boolean;
  liveUpdatesEnabled?: boolean;
  liveChanges?: LiveBookingChanges | null;
  animateInitialEntry?: boolean;
  onInitialEntryStarted?(): void;
  meetGreetCollectionDeliveryEnabled?: boolean;
  emptyMessage?: string;
}

export const BookingViewList = ({
  bookings,
  animationsEnabled,
  liveUpdatesEnabled = false,
  liveChanges = null,
  animateInitialEntry = false,
  onInitialEntryStarted,
  meetGreetCollectionDeliveryEnabled = false,
  emptyMessage,
}: BookingViewListProps) => {
  const {
    listElement,
    getBookingRef,
    shouldTrackBookingElements,
    initialEntryByBookingId,
    liveMotionByBookingId,
    exitingBookings,
    finishLiveEntry,
    finishLiveUpdatePhase,
    finishLiveExit,
  } = useBookingViewMotion({
    bookings,
    animationsEnabled,
    liveUpdatesEnabled,
    liveChanges,
    animateInitialEntry,
    onInitialEntryStarted,
  });
  const exitingHeight =
    bookings.length === 0 && exitingBookings.length > 0
      ? Math.max(0, ...exitingBookings.map(({ top, height }) => top + height))
      : undefined;

  return (
    <div
      ref={listElement}
      className={classes.bookingViewList}
      style={exitingHeight == null ? undefined : { minHeight: exitingHeight }}
    >
      {bookings.map((booking) => (
        <MemoBookingViewRow
          key={booking.uuid}
          booking={booking}
          initialEntry={initialEntryByBookingId?.get(booking.uuid)}
          isMeasuring={initialEntryByBookingId == null}
          liveMotion={liveMotionByBookingId.get(booking.uuid)}
          shouldTrackBookingElements={shouldTrackBookingElements}
          getBookingRef={getBookingRef}
          meetGreetCollectionDeliveryEnabled={
            meetGreetCollectionDeliveryEnabled
          }
          finishLiveEntry={finishLiveEntry}
          finishLiveUpdatePhase={finishLiveUpdatePhase}
        />
      ))}
      {exitingBookings.map(({ key, booking, top, left, width }) => (
        <div
          key={key}
          className={classes.cardLiveExiting}
          style={{ top, left, width }}
          ref={(node) => {
            node?.setAttribute("inert", "");
          }}
          aria-hidden="true"
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget) finishLiveExit(key);
          }}
        >
          <MemoBookingListItem
            booking={booking}
            meetGreetCollectionDeliveryEnabled={
              meetGreetCollectionDeliveryEnabled
            }
          />
        </div>
      ))}
      {bookings.length === 0 && exitingBookings.length === 0 && emptyMessage && (
        <div className={classes.pendingView}>{emptyMessage}</div>
      )}
    </div>
  );
};
